import React from 'react';
import { useEffect } from 'react';
import { GROUP_NAMES } from 'utils';
import type { PicksFilterValues, RiskLevel } from 'services/types';
import { t } from '../../helpers/componentHelper';

import './PicksFilters.scss';

type Props = {
  value: PicksFilterValues;
  userGroups: string[];
  disabled?: boolean;
  isInsightsPage?: boolean;
  onChange: (next: PicksFilterValues) => void;
  onRefresh?: () => void;
};

const SPORTS: { id: string; label: string }[] = [
  { id: 'nhl', label: 'NHL' },
  { id: 'nba', label: 'NBA' },
  { id: 'nfl', label: 'NFL' },
];

const LEG_OPTIONS: { id: '' | 2 | 3; label: string }[] = [
  { id: '', label: 'All' },
  { id: 2, label: '2 Legs' },
  { id: 3, label: '3 Legs' },
];

const RISK_OPTIONS: { id: '' | RiskLevel; label: string }[] = [
  { id: '', label: 'All' },
  { id: 'lock', label: 'Lock' },
  { id: 'fifty_fifty', label: '50/50' },
  { id: 'risky', label: 'Risky' },
];

const DATE_OPTIONS: { id: '' | 'today' | 'yesterday'; label: string }[] = [
  { id: '', label: 'All' },
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
];

const EVENT_DATE_OPTIONS: { id: '' | 'today' | 'tomorrow'; label: string }[] = [
  { id: '', label: 'All' },
  { id: 'today', label: 'Today' },
  { id: 'tomorrow', label: 'Tomorrow' },
];

const FAVORITE_OPTIONS: { id: '' | 'favorites'; label: string }[] = [
  { id: '', label: 'All' },
  { id: 'favorites', label: 'Favorites' },
];

function PicksFilters({ value, userGroups, disabled, isInsightsPage, onChange, onRefresh }: Props) {
  const [isFreeUser, setIsFreeUser] = React.useState(false);
  const update = (patch: Partial<PicksFilterValues>) => onChange({ ...value, ...patch });

  useEffect(() => {
    const isFreeUser = userGroups.includes(GROUP_NAMES.free);
    setIsFreeUser(isFreeUser);
  }, [userGroups]);

  return (
    <div className={'picksFilters picksFilters__container'}>
      <div className={'picksFilters__row--sport'}>
        <label className={'picksFilters__label'} htmlFor={'sport'}>
          Sport
        </label>
        <select
          id={'sport'}
          className={'picksFilters__select'}
          value={value.sport}
          onChange={(e) => update({ sport: e.target.value })}
          disabled={disabled}
        >
          {SPORTS.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
        <button
          type={'button'}
          className={'picksFilters__chip picksFilters__chip--refresh'}
          onClick={() => (onRefresh ? onRefresh() : null)}
          disabled={disabled}
          aria-label={'Refresh'}
        >
          &#10227;
        </button>
      </div>
      <div className={'picksFilters'}>
        <div className={'picksFilters__row'}>
          <span className={'picksFilters__label'}>{`${t(`pages.picks.pickFilters.numLegs`)}`}</span>
          <div className={'picksFilters__chips'} role={'radiogroup'} aria-label={'legs'}>
            {LEG_OPTIONS.filter((opt) => (isFreeUser ? opt.id !== 2 : true)).map((opt) => (
              <button
                key={opt.id || 'all'}
                id={`leg-${opt.id}`}
                type={'button'}
                role={'radio'}
                aria-checked={(value.parlayLegs ?? '') === opt.id}
                className={
                  (value.parlayLegs ?? '') === opt.id
                    ? 'picksFilters__chip picksFilters__chip--active'
                    : 'picksFilters__chip'
                }
                onClick={() =>
                  update({ parlayLegs: opt.id === '' ? undefined : (opt.id as 2 | 3) })
                }
                disabled={disabled}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        {!isInsightsPage && (
          <div className={'picksFilters__row'}>
            <span className={'picksFilters__label'}>{`${t(
              `pages.picks.pickFilters.riskLevel`,
            )}`}</span>
            <div className={'picksFilters__chips'} role={'radiogroup'} aria-label={'risk'}>
              {RISK_OPTIONS.filter((opt) => (isFreeUser ? opt.id !== 'lock' : true)).map((opt) => (
                <button
                  key={opt.id || 'all'}
                  id={opt.id}
                  type={'button'}
                  role={'radio'}
                  aria-checked={(value.riskLevel ?? '') === opt.id}
                  className={
                    (value.riskLevel ?? '') === opt.id
                      ? 'picksFilters__chip picksFilters__chip--active'
                      : 'picksFilters__chip'
                  }
                  onClick={() =>
                    update({ riskLevel: opt.id === '' ? undefined : (opt.id as RiskLevel) })
                  }
                  disabled={disabled}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {!isInsightsPage && (
          <div className={'picksFilters__row'}>
            <span className={'picksFilters__label'}>{`${t(
              `pages.picks.pickFilters.picksCreated`,
            )}`}</span>
            <div className={'picksFilters__chips'} role={'radiogroup'} aria-label={'dates'}>
              {DATE_OPTIONS.map((opt) => (
                <button
                  key={opt.id || 'all'}
                  id={opt.id}
                  type={'button'}
                  role={'radio'}
                  aria-checked={(value.computedDate ?? '') === opt.id}
                  className={
                    (value.computedDate ?? '') === opt.id
                      ? 'picksFilters__chip picksFilters__chip--active'
                      : 'picksFilters__chip'
                  }
                  onClick={() =>
                    update({
                      computedDate: opt.id === '' ? undefined : (opt.id as 'today' | 'yesterday'),
                    })
                  }
                  disabled={disabled}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {!isInsightsPage && (
          <div className={'picksFilters__row'}>
            <span className={'picksFilters__label'}>{`${t(
              `pages.picks.pickFilters.gameDate`,
            )}`}</span>
            <div className={'picksFilters__chips'} role={'radiogroup'} aria-label={'dates'}>
              {EVENT_DATE_OPTIONS.map((opt) => (
                <button
                  key={opt.id || 'all'}
                  id={opt.id}
                  type={'button'}
                  role={'radio'}
                  aria-checked={(value.eventDate ?? '') === opt.id}
                  className={
                    (value.eventDate ?? '') === opt.id
                      ? 'picksFilters__chip picksFilters__chip--active'
                      : 'picksFilters__chip'
                  }
                  onClick={() =>
                    update({
                      eventDate: opt.id === '' ? undefined : (opt.id as 'today' | 'tomorrow'),
                    })
                  }
                  disabled={disabled}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {!isInsightsPage && (
          <div className={'picksFilters__row'}>
            <span className={'picksFilters__label'}>{`${t(
              `pages.picks.pickFilters.favorite`,
            )}`}</span>
            <div className={'picksFilters__chips'} role={'radiogroup'} aria-label={'dates'}>
              {FAVORITE_OPTIONS.map((opt) => (
                <button
                  key={opt.id || 'all'}
                  id={opt.id}
                  type={'button'}
                  role={'radio'}
                  aria-checked={(value.isFavorite ?? '') === opt.id}
                  className={
                    (value.isFavorite ?? '') === opt.id
                      ? 'picksFilters__chip picksFilters__chip--active'
                      : 'picksFilters__chip'
                  }
                  onClick={() =>
                    update({
                      isFavorite: opt.id === '' ? undefined : (opt.id as 'favorites'),
                    })
                  }
                  disabled={disabled}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PicksFilters;
